import { IconAlertCircle } from '@developerz/ui';
import type { JSX } from 'solid-js';
import { t } from '../i18n';
import { day } from '../lib/time';
import { Button } from './ui/Button';

// "Email bouncing: update your address" (plan 101 N1): the persistent alert for
// an address the notification fanout stopped mailing after a hard bounce.
// Presenter: `EmailBounceAlert` owns the read and the retry write.
//
// It names BOTH fixes because the address lives in neither place it could be
// edited here: the user's primary email is GitHub's (refreshed at sign-in), and a
// different destination is an Email routes override. Suppression is keyed on the
// address, so either fix clears it on its own. Resume is the third door — same
// address, working again — and the server re-suppresses on the next hard bounce,
// so offering it costs one bounce at most.

export type EmailBounceNoticeProps = {
  readonly address: string;
  /** When the address was suppressed (ISO-8601). */
  readonly since: string;
  /** The org-scoped Email routes page. */
  readonly emailRoutesHref: string;
  /** A resume is in flight — the control is held. */
  readonly resuming: boolean;
  readonly onResume: () => void;
};

export function EmailBounceNotice(props: EmailBounceNoticeProps): JSX.Element {
  return (
    <div class="alert alert--icon alert--destructive" role="alert">
      <IconAlertCircle />
      <p class="alert__title">{t('email_bounce.title')}</p>
      <p class="alert__desc">
        {t('email_bounce.desc', { address: props.address, date: day(props.since) })}
      </p>
      <p class="alert__desc">{t('email_bounce.fix')}</p>
      <p class="alert__desc">
        <a href={props.emailRoutesHref}>{t('email_bounce.routes_link')}</a>{' '}
        <Button
          variant="outline"
          size="sm"
          disabled={props.resuming}
          aria-label={t('email_bounce.resume_label', { address: props.address })}
          onClick={() => props.onResume()}
        >
          {t('email_bounce.resume')}
        </Button>
      </p>
    </div>
  );
}
